import { useTranslation } from 'react-i18next';
import { Alert, Empty, Flex, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import {
  FilterBarAddButton,
  FilterBarActions,
  FilterBarRefreshButton,
} from '~/app/components/common/FilterBar/FilterBarToolbar';
import { FilterBar } from '~/app/components/common/FilterBar/FilterBar';
import { EvimoListPanel } from '~/app/components/common/EvimoListPanel/EvimoListPanel';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';
import type { CrudTableViewProps } from './CrudTable.types';

/**
 * Knowledge-base lookup editor: FilterBar + {@link EvimoTable} only (no alternate table).
 */
export const CrudTable = ({
  config,
  data,
  loading,
  error,
  onAddRow,
  onRefresh,
  tableColumns,
  pagination,
  onPageChange,
  searchValue,
  setSearchValue,
  onSearch,
  debounceRef,
}: CrudTableViewProps) => {
  const { t } = useTranslation('componentSchema');

  if (error) {
    return (
      <Flex vertical gap={12}>
        <Alert
          message={t('crudTable.failedToLoad')}
          description={error}
          type="error"
          showIcon
          action={
            <FilterBarRefreshButton
              size="small"
              ns="componentSchema"
              labelKey="crudTable.retry"
              defaultLabel="Retry"
              onClick={onRefresh}
            />
          }
        />
      </Flex>
    );
  }

  return (
    <EvimoListPanel>
      <FilterBar>
        {onSearch !== undefined ? (
          <Input
            allowClear
            className="evimo-filter-search"
            prefix={<SearchOutlined />}
            placeholder={t(
              'crudTable.searchPlaceholder',
              'Search by code or name...',
            )}
            value={searchValue}
            onChange={(event) => {
              const value = event.target.value;
              setSearchValue(value);
              if (debounceRef.current) {
                window.clearTimeout(debounceRef.current);
              }
              debounceRef.current = window.setTimeout(() => {
                onSearch(value);
              }, 350);
            }}
            onPressEnter={() => {
              if (debounceRef.current) {
                window.clearTimeout(debounceRef.current);
              }
              onSearch(searchValue);
            }}
          />
        ) : null}

        <FilterBarActions>
          <FilterBarRefreshButton
            ns="componentSchema"
            labelKey="crudTable.refresh"
            onClick={onRefresh}
          />
          {!config.readonly ? (
            <FilterBarAddButton
              ns="componentSchema"
              labelKey="crudTable.addRow"
              defaultLabel="Add row"
              onClick={onAddRow}
            />
          ) : null}
        </FilterBarActions>
      </FilterBar>

      <EvimoTable
        columns={tableColumns}
        dataSource={data.map((row) => ({ ...row, key: row._key }))}
        loading={loading}
        bordered
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('crudTable.emptyDescription')}
            />
          ),
        }}
        pagination={{
          current: pagination?.current ?? 1,
          pageSize: pagination?.pageSize ?? 10,
          total: pagination?.total,
        }}
        onChange={(pager) => {
          if (onPageChange) {
            onPageChange(pager.current || 1, pager.pageSize || 10);
          }
        }}
        scroll={{ x: 'max-content' }}
      />
    </EvimoListPanel>
  );
};
